/**
 * Canonical dashboard "metric" registry.
 *
 * A metric is the single, stable definition of the row set behind a dashboard
 * count card. Both the worker and the web use it:
 *   - the worker's dashboard count builders derive their WHERE clause from it
 *   - the worker's `/requirements` and `/actual-status` list routes apply the
 *     exact same WHERE clause when the client passes `?metric=<key>`
 *   - the web renders the card's drill-down as `?metric=<key>` and the list
 *     page resolves the metric back to the same filters
 *
 * Because the count query and the list query share one definition, a card can
 * never disagree with the list it drills into (status set, owner/actor rule,
 * self-exclusion all match by construction).
 *
 * This module is pure TypeScript with no runtime dependencies so it can be
 * imported from the worker and the web without bundling surprises.
 */

export type DashboardMetricEntity = "requirement" | "actual";

/**
 * How the metric restricts the row set to the acting user:
 *  - "created_by"          records authored by the current user
 *  - "manager_decided_by"  labour_requirements columns written on a manager
 *                          decision (manager_decision + manager_decided_by)
 *  - "cluster_decided_by"  labour_requirements columns written on a cluster
 *                          head decision (cluster_decision + cluster_decided_by)
 *  - "approval_actor"      approval_actions.performed_by (audit trail; used for
 *                          actual labour where the header has no decided_by col)
 *  - "assigned_to_self"    labour_requirements.assigned_pm_user_id equals the
 *                          current user, OR is NULL (legacy rows predating PM
 *                          assignment stay visible to any manager with the
 *                          PM-approval permission)
 */
export type DashboardMetricOwner =
  | "created_by"
  | "manager_decided_by"
  | "cluster_decided_by"
  | "approval_actor"
  | "assigned_to_self";

export interface DashboardMetricDefinition {
  /** Stable key. Matches the dashboard widget key for the linked cards. */
  key: string;
  entity: DashboardMetricEntity;
  /** Label shown on the list page's active-filter chip. */
  label: string;
  /** Current-status restriction. Absent = any status allowed by role scope. */
  statuses?: string[];
  /** Owner/actor restriction. Absent = whole scope. */
  owner?: DashboardMetricOwner;
  /**
   * Decision values for owner === "manager_decided_by" | "cluster_decided_by"
   * (e.g. ["APPROVE"]) or approval_actions.action values for
   * owner === "approval_actor" (e.g. ["manager_approve"]).
   */
  decisions?: string[];
  /** Exclude records authored by the current user (approval queues). */
  excludeSelf?: boolean;
  /** List route this card drills into. */
  route: "/requirements" | "/actuals";
}

/**
 * Every status except DRAFT. This is the visibility floor shared by the
 * Manager and Cluster Head list tiers (`status != 'DRAFT'`), so the decision
 * cards built on this set can never count a row their drill-down list hides.
 */
const NON_DRAFT_STATUSES = [
  "PENDING_MANAGER_APPROVAL",
  "MANAGER_APPROVED",
  "REJECTED_BY_MANAGER",
  "APPROVED",
  "REJECTED_BY_CLUSTER_HEAD",
];

/**
 * Every dashboard count card that links into a list page, keyed by the widget
 * key. Cards that link to admin module pages (projects/users) or to the
 * approvals page are deliberately not listed here.
 */
export const DASHBOARD_METRICS: readonly DashboardMetricDefinition[] = [
  // ── Summary (ORG_ADMIN overview) ────────────────────────────────────────
  { key: "requirements_total", entity: "requirement", label: "All Departmental Labour-Requirement", route: "/requirements" },
  { key: "requirements_approved", entity: "requirement", label: "Approved Departmental Labour-Requirement", statuses: ["APPROVED"], route: "/requirements" },
  { key: "actuals_total", entity: "actual", label: "All Departmental Labour -Actual", route: "/actuals" },
  { key: "actuals_approved", entity: "actual", label: "Approved Departmental Labour -Actual", statuses: ["APPROVED"], route: "/actuals" },
  { key: "actuals_approved_cost", entity: "actual", label: "Approved Departmental Labour -Actual Cost", statuses: ["APPROVED"], route: "/actuals" },

  // ── Author (Site Engineer): drafts + action required ───────────────────
  { key: "my_draft_requirements", entity: "requirement", label: "My Drafts", statuses: ["DRAFT"], owner: "created_by", route: "/requirements" },
  { key: "my_action_required_requirements", entity: "requirement", label: "My Action Required", statuses: ["REJECTED_BY_MANAGER", "REJECTED_BY_CLUSTER_HEAD"], owner: "created_by", route: "/requirements" },
  { key: "my_in_work_requirements", entity: "requirement", label: "Work Requiring Attention", statuses: ["DRAFT", "REJECTED_BY_MANAGER", "REJECTED_BY_CLUSTER_HEAD"], owner: "created_by", route: "/requirements" },
  { key: "my_draft_actuals", entity: "actual", label: "My Drafts (Departmental Labour -Actual)", statuses: ["DRAFT"], owner: "created_by", route: "/actuals" },
  { key: "my_action_required_actuals", entity: "actual", label: "My Action Required (Departmental Labour -Actual)", statuses: ["REJECTED_BY_MANAGER", "REJECTED_BY_CLUSTER_HEAD"], owner: "created_by", route: "/actuals" },
  { key: "my_in_work_actuals", entity: "actual", label: "Work Requiring Attention", statuses: ["DRAFT", "REJECTED_BY_MANAGER", "REJECTED_BY_CLUSTER_HEAD"], owner: "created_by", route: "/actuals" },

  // ── Manager approval queues (reuse the /pending/approvals predicates) ──
  { key: "awaiting_manager_requirements", entity: "requirement", label: "Departmental Labour-Requirement Pending My Approval", statuses: ["PENDING_MANAGER_APPROVAL"], owner: "assigned_to_self", excludeSelf: true, route: "/requirements" },
  { key: "awaiting_manager_actuals", entity: "actual", label: "Departmental Labour -Actual Pending My Approval", statuses: ["PENDING_MANAGER_APPROVAL"], excludeSelf: true, route: "/actuals" },

  // ── Cluster Head / final approval queues ───────────────────────────────
  { key: "awaiting_cluster_requirements", entity: "requirement", label: "Departmental Labour-Requirement Pending Final Approval", statuses: ["MANAGER_APPROVED"], route: "/requirements" },
  { key: "awaiting_cluster_actuals", entity: "actual", label: "Departmental Labour -Actual Pending Final Approval", statuses: ["MANAGER_APPROVED"], route: "/actuals" },

  // ── "by Me" decisions (Manager / Cluster Head) ─────────────────────────
  // Rejected cards count ONLY records whose CURRENT rejection state belongs to
  // the logged-in approver:
  //   - requirements carry the deciding columns (manager_decision +
  //     manager_decided_by / cluster_decision + cluster_decided_by) written on
  //     the decision, so the current-rejection author is read directly from the
  //     parent (never the lifetime approval trail)
  //   - actual labour headers have no decided_by column, so the current
  //     rejection author comes from the approval_actions actor. This is exact
  //     because actual rejections are terminal (only DRAFT actuals are
  //     editable) - the current status can never be re-reached by another
  //     actor after a resubmission.
  // Approved cards count distinct current parents on which the logged-in
  // approver performed the matching approve action. They keep NO status
  // restriction beyond the approver-tier visibility floor (non-DRAFT): once a
  // PM approves a record it stays "approved by me" even after the Cluster Head
  // later rejects or finally approves it. DRAFT is excluded only to mirror the
  // Manager/Cluster Head list tier (`status != 'DRAFT'`), so the count card
  // and its drill-down list can never disagree - a PM-approved requirement can
  // return to DRAFT when an engineer edits a CH-rejected version.
  { key: "manager_rejected_by_me_req", entity: "requirement", label: "Departmental Labour-Requirement Rejected by Me", statuses: ["REJECTED_BY_MANAGER"], owner: "manager_decided_by", decisions: ["REJECT"], route: "/requirements" },
  { key: "manager_rejected_by_me_act", entity: "actual", label: "Departmental Labour -Actual Rejected by Me", statuses: ["REJECTED_BY_MANAGER"], owner: "approval_actor", decisions: ["manager_reject"], route: "/actuals" },
  { key: "manager_approved_by_me_req", entity: "requirement", label: "Departmental Labour-Requirement Approved by Me", statuses: NON_DRAFT_STATUSES, owner: "approval_actor", decisions: ["manager_approve"], route: "/requirements" },
  { key: "manager_approved_by_me_act", entity: "actual", label: "Departmental Labour -Actual Approved by Me", statuses: NON_DRAFT_STATUSES, owner: "approval_actor", decisions: ["manager_approve"], route: "/actuals" },
  { key: "cluster_rejected_by_me_req", entity: "requirement", label: "Departmental Labour-Requirement Rejected by Me", statuses: ["REJECTED_BY_CLUSTER_HEAD"], owner: "cluster_decided_by", decisions: ["REJECT"], route: "/requirements" },
  { key: "cluster_rejected_by_me_act", entity: "actual", label: "Departmental Labour -Actual Rejected by Me", statuses: ["REJECTED_BY_CLUSTER_HEAD"], owner: "approval_actor", decisions: ["cluster_reject"], route: "/actuals" },
  { key: "cluster_approved_by_me_req", entity: "requirement", label: "Departmental Labour-Requirement Finally Approved by Me", statuses: NON_DRAFT_STATUSES, owner: "approval_actor", decisions: ["cluster_approve"], route: "/requirements" },
  { key: "cluster_approved_by_me_act", entity: "actual", label: "Departmental Labour -Actual Finally Approved by Me", statuses: NON_DRAFT_STATUSES, owner: "approval_actor", decisions: ["cluster_approve"], route: "/actuals" },

  // ── Site Engineer six-card status summary (created_by current user) ──────
  // The single canonical owner-scoped definition behind each of the six cards.
  // Total Requirements / Total Actual Labour are the distinct totals with NO
  // status restriction; they intentionally overlap the four status cards (see
  // count reconciliation). The status cards use the CURRENT normalized parent
  // status - never the lifetime approval-history trail - so resubmission moves
  // a record to the correct card automatically. Requirements treat rejected
  // statuses as editable/resubmittable while actual labour rejections are
  // permanently closed (only DRAFT records are editable); both still count in
  // the totals and in their current-status rejection card.
  { key: "site_total_req", entity: "requirement", label: "All My Departmental Labour-Requirement", owner: "created_by", route: "/requirements" },
  { key: "site_total_act", entity: "actual", label: "All My Departmental Labour -Actual", owner: "created_by", route: "/actuals" },
  { key: "site_pending_pm_req", entity: "requirement", label: "My Departmental Labour-Requirement Pending PM Approval", statuses: ["PENDING_MANAGER_APPROVAL"], owner: "created_by", route: "/requirements" },
  { key: "site_pending_pm_act", entity: "actual", label: "My Departmental Labour -Actual Pending PM Approval", statuses: ["PENDING_MANAGER_APPROVAL"], owner: "created_by", route: "/actuals" },
  { key: "site_pending_ch_req", entity: "requirement", label: "My Departmental Labour-Requirement Pending CH Approval", statuses: ["MANAGER_APPROVED"], owner: "created_by", route: "/requirements" },
  { key: "site_pending_ch_act", entity: "actual", label: "My Departmental Labour -Actual Pending CH Approval", statuses: ["MANAGER_APPROVED"], owner: "created_by", route: "/actuals" },
  { key: "site_rejected_pm_req", entity: "requirement", label: "My Departmental Labour-Requirement Rejected by PM", statuses: ["REJECTED_BY_MANAGER"], owner: "created_by", route: "/requirements" },
  { key: "site_rejected_pm_act", entity: "actual", label: "My Departmental Labour -Actual Rejected by PM", statuses: ["REJECTED_BY_MANAGER"], owner: "created_by", route: "/actuals" },
  { key: "site_rejected_ch_req", entity: "requirement", label: "My Departmental Labour-Requirement Rejected by CH", statuses: ["REJECTED_BY_CLUSTER_HEAD"], owner: "created_by", route: "/requirements" },
  { key: "site_rejected_ch_act", entity: "actual", label: "My Departmental Labour -Actual Rejected by CH", statuses: ["REJECTED_BY_CLUSTER_HEAD"], owner: "created_by", route: "/actuals" },
];

export const DASHBOARD_METRIC_BY_KEY: Readonly<Record<string, DashboardMetricDefinition>> =
  Object.fromEntries(DASHBOARD_METRICS.map((m) => [m.key, m])) as Record<string, DashboardMetricDefinition>;

/**
 * The single-entity metric behind each of the two "Total" cards.
 */
export const SITE_TOTAL_CARD_METRICS = {
  req: "site_total_req",
  act: "site_total_act",
} as const;

/**
 * The per-entity metrics behind each combined (Requirements + Actual Labour)
 * card. The worker sums the two counts for the card value; the web uses the two
 * metric keys to build the popover drill-downs (exact status + owner filters).
 */
export const SITE_COMBINED_CARD_METRICS: Readonly<Record<string, { req: string; act: string }>> = {
  site_pending_pm: { req: "site_pending_pm_req", act: "site_pending_pm_act" },
  site_pending_ch: { req: "site_pending_ch_req", act: "site_pending_ch_act" },
  site_rejected_pm: { req: "site_rejected_pm_req", act: "site_rejected_pm_act" },
  site_rejected_ch: { req: "site_rejected_ch_req", act: "site_rejected_ch_act" },
};

/**
 * The per-entity metrics behind each combined "by Me" approval card. The worker
 * sums the two counts for the card value; the web uses the two metric keys to
 * build the popover drill-downs (exact current-state + actor filters).
 */
export const DECISION_CARD_METRICS: Readonly<Record<string, { req: string; act: string }>> = {
  manager_decisions_rejected_by_me: { req: "manager_rejected_by_me_req", act: "manager_rejected_by_me_act" },
  manager_decisions_approved_by_me: { req: "manager_approved_by_me_req", act: "manager_approved_by_me_act" },
  cluster_decisions_rejected_by_me: { req: "cluster_rejected_by_me_req", act: "cluster_rejected_by_me_act" },
  cluster_decisions_approved_by_me: { req: "cluster_approved_by_me_req", act: "cluster_approved_by_me_act" },
};

export function getDashboardMetric(key: string | null | undefined): DashboardMetricDefinition | undefined {
  if (!key) return undefined;
  return DASHBOARD_METRIC_BY_KEY[key];
}

/** Drill-down path for a metric (used by the dashboard registry). */
export function metricDrillPath(metric: DashboardMetricDefinition): string {
  return `${metric.route}?metric=${encodeURIComponent(metric.key)}`;
}

/** Order the statuses the way the workflow reads (used for stable SQL IN). */
export function orderedStatuses(statuses: string[]): string[] {
  const ORDER = [
    "DRAFT",
    "PENDING_MANAGER_APPROVAL",
    "MANAGER_APPROVED",
    "REJECTED_BY_MANAGER",
    "APPROVED",
    "REJECTED_BY_CLUSTER_HEAD",
  ];
  return ORDER.filter((s) => statuses.includes(s));
}
